import { api } from './api';

interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination?: { total?: number; totalItems?: number; page?: number; pageSize?: number };
}

/** One declared input/output field of a function's I/O contract. */
export interface FunctionIOField {
  name: string;
  dataType: string;
  isRequired?: boolean | null;
  isCollection?: boolean | null;
  description?: string | null;
}

/** A function_master row — a reusable resolution capability + its I/O contract. */
export interface FunctionMaster {
  _id: string;
  functionCode: string;
  description?: string | null;
  functionType: string;   // REGISTERED_HANDLER | IMPORT_PATH | QUERY | API_CALL | EXPRESSION
  capability: string;     // RECIPIENT_RESOLUTION | EVAL_TARGET
  isActive?: boolean | null;
  inputDefinition?: FunctionIOField[] | null;
  outputDefinition?: FunctionIOField[] | null;
  configuration?: Record<string, unknown> | null;
  defaultTimeoutSeconds?: number | null;
  appId?: string | null;
  isDelete?: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface FunctionCreateRequest {
  functionCode: string;
  functionType: string;
  capability: string;
  description?: string | null;
  inputDefinition?: FunctionIOField[];
  outputDefinition?: FunctionIOField[];
  configuration?: Record<string, unknown>;
  defaultTimeoutSeconds?: number | null;
  appId?: string;
}

export type FunctionUpdateRequest = Partial<Omit<FunctionCreateRequest, 'appId'>>;

/** Structured result of a test run. `ok:false` is a function that ran and failed
 *  (or a type that cannot be tested) — not a transport error. */
export interface FunctionTestResult {
  ok: boolean;
  functionType: string;
  output: unknown;
  usage?: Record<string, unknown> | null;
  toolCalls?: unknown[] | null;
  retrievalContext?: unknown[] | null;
  error?: string | null;
  errorCode?: string | null;
  durationMs: number;
}

export interface FunctionListParams {
  page?: number;
  page_size?: number;
  search?: string;
  include_deleted?: boolean;
  capability?: string;   // e.g. EVAL_TARGET for the eval picker
  app_id?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: string;
}

export const functionsService = {
  list: async (params?: FunctionListParams): Promise<PaginatedResponse<FunctionMaster>> => {
    const response = await api.get<PaginatedResponse<FunctionMaster>>('/api/v1/functions/list', { params });
    return response.data;
  },
  getById: async (id: string): Promise<FunctionMaster> => {
    const response = await api.get<ApiResponse<FunctionMaster>>(`/api/v1/functions/get/${id}`);
    return response.data.data;
  },
  create: async (data: FunctionCreateRequest): Promise<FunctionMaster> => {
    const response = await api.post<ApiResponse<FunctionMaster>>('/api/v1/functions/create', data);
    return response.data.data;
  },
  update: async (id: string, data: FunctionUpdateRequest): Promise<FunctionMaster> => {
    const response = await api.put<ApiResponse<FunctionMaster>>(`/api/v1/functions/update/${id}`, data);
    return response.data.data;
  },
  softDelete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/api/v1/functions/delete/${id}`);
    return response.data.data;
  },
  restore: async (id: string): Promise<{ message: string }> => {
    const response = await api.patch<ApiResponse<{ message: string }>>(`/api/v1/functions/restore/${id}`);
    return response.data.data;
  },
  /**
   * Test-run a function with supplied inputs. `params` is keyed by the function's
   * inputDefinition names and passed straight through (bypassing parameterMapping).
   * A function that fails at runtime resolves with `ok:false` and a reason — only a
   * transport/HTTP error rejects.
   */
  test: async (id: string, params: Record<string, unknown>): Promise<FunctionTestResult> => {
    const response = await api.post<ApiResponse<FunctionTestResult>>(`/api/v1/functions/test/${id}`, { params });
    return response.data.data;
  },
  /**
   * Handler keys a REGISTERED_HANDLER function may name.
   *
   * The allowlist lives in code — a handlerKey selects an entry and is never
   * imported or evaluated — so this is the only way a form can offer what
   * exists rather than inviting a value the save will refuse.
   */
  handlers: async (): Promise<string[]> => {
    const response = await api.get<{ data?: { handlers?: string[] } }>(
      '/api/v1/notification-engine/registries/handlers',
    );
    return response.data?.data?.handlers || [];
  },
};
